/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * usePredictedScore - React hook for SAT score predictions
 *
 * Provides easy access to predicted SAT scores with automatic data fetching,
 * caching, and refresh capabilities.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import predictionService from '../services/predictionService'

import { createLogger } from '../utils/logger'

const logger = createLogger('usePredictedScore')

/**
 * Maps API errors to user-friendly messages
 * Provides specific guidance based on error type
 *
 * @param {Error} err - The error object from the API call
 * @returns {string} User-friendly error message
 */
const getErrorMessage = (err) => {
  // HTTP status-based errors
  if (err.response?.status === 401) {
    return 'Please sign in to view predictions'
  }
  if (err.response?.status === 403) {
    return 'You do not have permission to view this prediction'
  }
  if (err.response?.status === 404) {
    return 'Prediction data not available'
  }
  if (err.response?.status === 429) {
    return 'Too many requests. Please wait a moment and try again'
  }
  if (err.response?.status >= 500) {
    return 'Server error. Please try again later'
  }

  // Network/connection errors
  if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
    return 'Request timed out. Please check your connection'
  }
  if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
    return 'Network error. Please check your connection'
  }

  // API response message (if provided and not generic)
  const apiMessage = err.response?.data?.message
  if (apiMessage && !apiMessage.includes('Internal') && !apiMessage.includes('Error')) {
    return apiMessage
  }

  // Default fallback
  return 'Unable to load prediction. Please try again'
}

/**
 * Hook for fetching and managing SAT score predictions
 *
 * @param {Object} options - Hook configuration
 * @param {string} options.strategyType - Specific strategy ('SAT_MATH', 'SAT_READING_WRITING'), or null for combined
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (0 = disabled)
 * @returns {Object} Prediction state and methods
 */
export function usePredictedScore({
  strategyType = null,
  autoFetch = true,
  refreshInterval = 0
} = {}) {
  // State
  const [prediction, setPrediction] = useState(null)
  const [loading, setLoading] = useState(autoFetch)
  const [error, setError] = useState(null)
  const [lastFetched, setLastFetched] = useState(null)

  // Refs for interval management and race condition prevention
  const refreshIntervalRef = useRef(null)
  const isMountedRef = useRef(true)
  const requestIdRef = useRef(0)

  /**
   * Fetch prediction data
   * Uses requestId to prevent race conditions when strategyType changes rapidly
   */
  const fetchPrediction = useCallback(async () => {
    if (!isMountedRef.current) return

    // Increment request ID to track this specific request
    const currentRequestId = ++requestIdRef.current

    setLoading(true)
    setError(null)

    try {
      let response
      if (strategyType) {
        // Fetch specific strategy prediction
        response = await predictionService.getStrategyPrediction(strategyType)
      } else {
        // Fetch combined prediction
        response = await predictionService.getCombinedPrediction()
      }

      // Only update state if this is still the latest request and component is mounted
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setPrediction(response?.data || response)
        setLastFetched(new Date())
      }
    } catch (err) {
      logger.error('Error fetching prediction:', err)
      // Only update error state if this is still the latest request
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setError(getErrorMessage(err))
      }
    } finally {
      // Only clear loading if this is still the latest request
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [strategyType])

  /**
   * Save current prediction to history
   */
  const savePrediction = useCallback(
    async (triggeredBy = 'manual_request', challengeId = null) => {
      if (!strategyType) {
        logger.warn('Cannot save prediction without specific strategyType')
        return null
      }

      try {
        const response = await predictionService.savePrediction(
          strategyType,
          triggeredBy,
          challengeId
        )
        return response?.data || response
      } catch (err) {
        logger.error('Error saving prediction:', err)
        throw err
      }
    },
    [strategyType]
  )

  /**
   * Manually refresh prediction
   */
  const refresh = useCallback(() => {
    return fetchPrediction()
  }, [fetchPrediction])

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchPrediction()
    }
  }, [autoFetch, fetchPrediction])

  // Set up refresh interval
  useEffect(() => {
    if (refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(fetchPrediction, refreshInterval)
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }
  }, [refreshInterval, fetchPrediction])

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return {
    prediction,
    loading,
    error,
    lastFetched,
    refresh,
    savePrediction,
    // Computed properties
    hasEnoughData: prediction?.hasEnoughData ?? false,
    predictedScore: prediction?.predictedScore ?? null,
    predictedTotal: prediction?.predictedTotal ?? null,
    confidence: prediction?.confidence ?? null,
    confidenceLevel: prediction?.confidenceLevel ?? null,
    trend: prediction?.trend ?? null,
    scoreRange: prediction?.scoreRange ?? null
  }
}

/**
 * Hook for fetching prediction history
 *
 * @param {Object} options - Hook configuration
 * @param {string} options.strategyType - Filter by strategy type
 * @param {number} options.limit - Max records per page
 * @param {boolean} options.autoFetch - Whether to fetch on mount
 * @returns {Object} History state and methods
 */
export function usePredictionHistory({ strategyType = null, limit = 20, autoFetch = true } = {}) {
  // State
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(autoFetch)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({ total: 0, offset: 0, limit, hasMore: false })
  const [currentOffset, setCurrentOffset] = useState(0)

  // Refs for mount state and race condition prevention
  const isMountedRef = useRef(true)
  const requestIdRef = useRef(0)

  /**
   * Fetch history page
   * Uses requestId to prevent race conditions when parameters change rapidly
   */
  const fetchHistory = useCallback(
    async (offset = 0) => {
      if (!isMountedRef.current) return

      // Increment request ID to track this specific request
      const currentRequestId = ++requestIdRef.current

      setLoading(true)
      setError(null)

      try {
        const response = await predictionService.getHistory({
          strategyType,
          limit,
          offset
        })

        const data = response?.data || response

        // Only update state if this is still the latest request
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setHistory(data?.predictions || [])
          setPagination({
            total: data?.pagination?.total || 0,
            offset: data?.pagination?.offset || 0,
            limit: data?.pagination?.limit || limit,
            hasMore: data?.pagination?.hasMore || false
          })
          setCurrentOffset(offset)
        }
      } catch (err) {
        logger.error('Error fetching prediction history:', err)
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setError(getErrorMessage(err))
        }
      } finally {
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    [strategyType, limit]
  )

  /**
   * Load next page
   */
  const loadMore = useCallback(() => {
    const nextOffset = currentOffset + limit
    if (nextOffset < pagination.total) {
      fetchHistory(nextOffset)
    }
  }, [currentOffset, limit, pagination.total, fetchHistory])

  /**
   * Load previous page
   */
  const loadPrevious = useCallback(() => {
    const prevOffset = Math.max(0, currentOffset - limit)
    fetchHistory(prevOffset)
  }, [currentOffset, limit, fetchHistory])

  /**
   * Go to specific page
   */
  const goToPage = useCallback(
    (page) => {
      const offset = page * limit
      fetchHistory(offset)
    },
    [limit, fetchHistory]
  )

  /**
   * Refresh current page
   */
  const refresh = useCallback(() => {
    return fetchHistory(currentOffset)
  }, [fetchHistory, currentOffset])

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchHistory(0)
    }
  }, [autoFetch, fetchHistory])

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return {
    history,
    loading,
    error,
    pagination,
    currentPage: Math.floor(currentOffset / limit),
    totalPages: Math.ceil(pagination.total / limit),
    hasNextPage: currentOffset + limit < pagination.total,
    hasPreviousPage: currentOffset > 0,
    loadMore,
    loadPrevious,
    goToPage,
    refresh
  }
}

/**
 * Hook for getting latest predictions across all strategies
 *
 * @param {Object} options - Hook configuration
 * @param {boolean} options.autoFetch - Whether to fetch on mount
 * @returns {Object} Latest predictions state and methods
 */
export function useLatestPredictions({ autoFetch = true } = {}) {
  const [predictions, setPredictions] = useState({})
  const [loading, setLoading] = useState(autoFetch)
  const [error, setError] = useState(null)

  // Refs for mount state and race condition prevention
  const isMountedRef = useRef(true)
  const requestIdRef = useRef(0)

  const fetchLatest = useCallback(async () => {
    if (!isMountedRef.current) return

    // Increment request ID to track this specific request
    const currentRequestId = ++requestIdRef.current

    setLoading(true)
    setError(null)

    try {
      const response = await predictionService.getLatest()
      const data = response?.data || response

      // Only update state if this is still the latest request
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setPredictions(data || {})
      }
    } catch (err) {
      logger.error('Error fetching latest predictions:', err)
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setError(getErrorMessage(err))
      }
    } finally {
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (autoFetch) {
      fetchLatest()
    }
  }, [autoFetch, fetchLatest])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  return {
    predictions,
    loading,
    error,
    refresh: fetchLatest,
    // Convenience accessors
    mathPrediction: predictions.SAT_MATH || null,
    mathMiniPrediction: predictions.SAT_MATH_MINI || null,
    rwPrediction: predictions.SAT_READING_WRITING || null,
    rwMiniPrediction: predictions.SAT_READING_WRITING_MINI || null
  }
}

export default usePredictedScore
